import { useFileUpload } from '@shared/hooks/use-file-upload'
import { attempt } from '@shared/utils'
import { useCallback, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { IssueDescriptionFields } from '../config/issue-description-fields'
import { MAX_ATTACHMENTS } from '../config/issue-description-fields'
import type { ContactFormData } from '../context/form-data-context'
import { useFormData } from '../context/form-data-context'
import { usePageFlow } from '../context/page-flow-context'
import { useAppData } from '@app/providers'
import { getSubmitErrorTranslationKey } from '../utils/submit-error.utils'
import { useSubmitContactRequest } from './use-submit-contact-request'

export const useIssueDescription = () => {
  const { t } = useTranslation()
  const { channel, setNextConfig, registerOnNext, goNext, setNextLoading, visibleStepCount, currentStepIndex } = usePageFlow()
  const { formData, updateFormData } = useFormData()
  const { setShowOrderNumberInput } = useAppData()
  const { submitContactRequest } = useSubmitContactRequest()
  const [submitError, setSubmitError] = useState<string | null>(null)

  const attachments = formData.channel === 'email' ? formData.attachments : []

  const { entries, isUploading, uploadedAssets, addFiles, removeEntry } = useFileUpload({
    maxFiles: MAX_ATTACHMENTS,
    initialAssets: attachments,
  })

  const intentId = formData.intentId
  const isLastStep = currentStepIndex === visibleStepCount - 1
  const getSubmitErrorMessage = useCallback((error: unknown) => t(getSubmitErrorTranslationKey(error, channel)), [t, channel])

  const {
    control,
    watch,
    setValue,
    formState: { isValid },
  } = useForm<IssueDescriptionFields>({
    mode: 'onChange',
    defaultValues: {
      description: formData.description,
      selectedLineItemIds: formData.selectedLineItemIds,
      selectedWorkIds: formData.selectedWorkIds,
      orderNumber: formData.orderNumber,
      websiteDomain: formData.websiteDomain,
    },
  })

  useEffect(() => {
    return () => setShowOrderNumberInput(false)
  }, [setShowOrderNumberInput])

  useEffect(() => {
    if (intentId !== 'help_with_my_order') {
      setValue('selectedLineItemIds', [])
      setValue('orderNumber', '')
      updateFormData({ selectedLineItemIds: [], orderNumber: '' })
    }
    if (intentId !== 'help_with_my_design') {
      setValue('selectedWorkIds', [])
      updateFormData({ selectedWorkIds: [] })
    }
    if (intentId !== 'website_and_domains') {
      setValue('websiteDomain', '')
      updateFormData({ websiteDomain: '' })
    }
  }, [intentId, setValue, updateFormData])

  useEffect(() => {
    const subscription = watch((values) => {
      updateFormData(values as Partial<ContactFormData>)
    })
    return () => subscription.unsubscribe()
  }, [watch, updateFormData])

  useEffect(() => {
    updateFormData({ attachments: uploadedAssets })
  }, [uploadedAssets, updateFormData])

  const submitCurrentStep = useCallback(async () => {
    setSubmitError(null)
    setNextLoading(true)

    const { data: result, error } = await attempt(submitContactRequest(formData))

    setNextLoading(false)

    if (error) {
      setSubmitError(getSubmitErrorMessage(error))
      return
    }

    if (!result.success) {
      setSubmitError(getSubmitErrorMessage(result.error))
      return
    }

    goNext()
  }, [formData, getSubmitErrorMessage, goNext, setNextLoading, submitContactRequest])

  useEffect(() => {
    registerOnNext(async () => {
      if (!isLastStep) {
        goNext()
        return
      }

      await submitCurrentStep()
    })
  }, [goNext, isLastStep, registerOnNext, submitCurrentStep])

  useEffect(() => {
    setNextConfig({
      disabled: !isValid || isUploading,
      label: isLastStep ? t('contact_us.page.actions.submit') : undefined,
    })
  }, [isValid, isUploading, isLastStep, setNextConfig, t])

  return {
    control,
    channel,
    intentId,
    entries,
    isUploading,
    addFiles,
    removeEntry,
    submitError,
  }
}
